/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import sortby from 'lodash.sortby';
import {translate} from '@ion/core/src/locale/locale';
import {
  MESSAGE_TYPES,
  SC_DEVICE_TYPES,
  SensorType,
  SensorUnits
} from '@ion/core/src/types/entities';
import {genericUnitConversion} from '@ion/core/src/utils/Conversions';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useDeviceSensors} from '@ion/query/src/hooks/useDeviceSensors';
import {systemOfUnitsSelector} from '@/store/selectors/login-selectors';
import {selectedAssetSelector} from '@/store/selectors/tracking-selectors';
import {ChartsAccordion} from './ChartsAccordion';
import {ChartsTooltip} from './ChartsTooltip';
import {ShockSensorAccordion} from './ShockSensorAccordion';
import TiltSensorAccordion from './TiltSensorAccordion';

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    loader : {
      height : '192px'
    }
  };
});

/**
 * SCDeviceSensorGraphs is a fully contained component that displays the sensor data for a selected
 * SC Device.
 *
 * ```typescript
 *
 * import SCDeviceSensorGraphs from './SCDeviceSensorGraphs';
 *
 * <SCDeviceSensorGraphs />
 *
 * ```
 */
const SCDeviceSensorGraphs = () => {
  const classes                   = useStyles();
  const asset                     = useSelector(selectedAssetSelector);
  const systemOfUnits             = useSelector(systemOfUnitsSelector);
  const celsius                   = SensorUnits.C;
  const shock                     = translate('tracking:scDeviceSensorTitle.shock');
  const tilt                      = translate('tracking:scDeviceSensorTitle.tilt');
  const queryData                 = useDeviceSensors({
    msgType  : MESSAGE_TYPES.LOCO_MESSAGE,
    deviceId : asset?.isScDevice?.() ? asset?.id?.()?.toString() : null,
    systemOfUnits
  });
  const shockData = queryData?.reports?.events?.filter(
    event => event?.event?.type === 'impact' || event?.event?.type === 'drop'
  );
  const tiltData = queryData?.reports?.events?.filter(
    event => event?.event?.type === 'tip'
  );
  const {maxImpact, maxTip}        = shockData?.[0]?.settings || {};
  const isSensorDataLoaded         = queryData?.status === 'success';
  const scDeviceSensors            = SC_DEVICE_TYPES[
    queryData?.reports?.currentReport?.locoEvent?.deviceDataConverted?.pegBehavior?.source?.title
  ];
  const noSensorData               = isNil(scDeviceSensors);
  const shouldDisplayTemperature   = scDeviceSensors?.includes(SensorType.Temperature) || noSensorData;
  const shouldDisplayLight         = scDeviceSensors?.includes(SensorType.Light) || noSensorData;
  const shouldDisplayShock         = scDeviceSensors?.includes(SensorType.Shock) || noSensorData;
  const shouldDisplayTilt          = scDeviceSensors?.includes(SensorType.Tilt) || noSensorData;
  const {accumulatedResults}       = queryData?.reports || {};
  const lightLevelData             = accumulatedResults?.lightLevel?.data || [];
  const accumulatedTemperatureData = accumulatedResults?.temperature?.data || [];
  const timeSeriesData             = queryData?.reports?.timeSeriesDataResults || [];

  // We need to combine the temperature data in timeSeriesData and accumulatedResults
  const allTemperatureData = useMemo(() => {
    const combinedTemperatureData = [...accumulatedTemperatureData, ...timeSeriesData];
    const uniqueTemperatureData = removeDuplicate('eventTime', combinedTemperatureData || []);
    return sortby(uniqueTemperatureData, 'eventTime');
  }, [accumulatedTemperatureData, timeSeriesData]);

  const getThresholdMinAndMax = (sensorType) => {
    const threshold = {max: 0, min: 0};
    const {
      maxTemperature,
      minTemperature,
      maxLightLevel,
      minLightLevel
    } = queryData?.reports?.settings || {};
    if (sensorType === 'temperature') {
      threshold.max = genericUnitConversion(parseFloat(maxTemperature), celsius, systemOfUnits);
      threshold.min = genericUnitConversion(parseFloat(minTemperature), celsius, systemOfUnits);
    } else if (sensorType === 'lightLevel') {
      threshold.max = maxLightLevel;
      threshold.min = minLightLevel;
    }
    return threshold;
  };

  const temperatureDataForDevice = useMemo(() => ({
    title       : SensorType.Temperature,
    sensorType  : SensorType.Temperature,
    data        : allTemperatureData,
    noDataLabel : translate('tracking:ionTagDetailsPanel.noDataAvailable'),
    threshold   : getThresholdMinAndMax(SensorType.Temperature)
  }), [accumulatedTemperatureData, timeSeriesData]);

  const temperatureData = useMemo(() => ({
    id        : temperatureDataForDevice.sensorType,
    data      : temperatureDataForDevice.data,
    threshold : temperatureDataForDevice.threshold
  }), [temperatureDataForDevice?.data]);

  const lightLevelDataForDevice = useMemo(() => ({
    title       : SensorType.Light,
    sensorType  : SensorType.Light,
    data        : sortby(lightLevelData, 'eventTime'),
    noDataLabel : translate('tracking:ionTagDetailsPanel.noDataAvailable'),
    threshold   : getThresholdMinAndMax(SensorType.Light)
  }), [lightLevelData]);

  const lightData = useMemo(() => ({
    id        : lightLevelDataForDevice.sensorType,
    data      : lightLevelDataForDevice.data,
    threshold : lightLevelDataForDevice.threshold
  }), [lightLevelDataForDevice?.data]);

  const shockDataForDevice = useMemo(() => {
    return {
      title       : shock,
      sensorType  : shock,
      data        : shockData,
      noDataLabel : translate('tracking:ionTagDetailsPanel.noDataAvailable'),
      threshold   : {max: maxImpact, min: 0}
    };
  }
  , [shockData, maxImpact]);

  const tiltDataForDevice = useMemo(() => {
    return {
      title       : tilt,
      sensorType  : tilt,
      data        : tiltData,
      noDataLabel : translate('tracking:ionTagDetailsPanel.noDataAvailable'),
      threshold   : {max: maxTip, min: 0}
    };
  }, [tiltData, maxTip]);

  if (isSensorDataLoaded) {
    return (
      <>
        {shouldDisplayTemperature && (
          <ChartsAccordion title={temperatureDataForDevice.title}
            data={temperatureData}
            loading={queryData?.loading}
            customToolTip={ChartsTooltip}
            noDataLabel={temperatureDataForDevice.noDataLabel}
            isAssetDetailsCard
            containTooltip />
        )}
        {shouldDisplayLight && (
          <ChartsAccordion title={lightLevelDataForDevice.title}
            data={lightData}
            loading={queryData?.loading}
            customToolTip={ChartsTooltip}
            noDataLabel={lightLevelDataForDevice.noDataLabel}
            isAssetDetailsCard
            containTooltip />
        )}
        {shouldDisplayShock && (
          <ShockSensorAccordion title={shockDataForDevice.title}
            data={{
              id        : shockDataForDevice.sensorType,
              data      : shockDataForDevice.data,
              threshold : shockDataForDevice.threshold
            }}
            loading={queryData?.loading}
            isAssetDetailsCard/>
        )}
        {shouldDisplayTilt && (
          <TiltSensorAccordion title={tiltDataForDevice.title}
            data={{
              id        : tiltDataForDevice.sensorType,
              data      : tiltDataForDevice.data,
              threshold : tiltDataForDevice.threshold
            }}
            loading={queryData?.loading}
            noDataLabel={shockDataForDevice.noDataLabel}
            isAssetDetailsCard/>
        )}
      </>
    );
  }
  return (
    <div className={classes.loader}>
      <TombstoneLoader />
    </div>
  );
};

export default SCDeviceSensorGraphs;
